import {
  ArrowUpRight,
  ShieldCheck,
  GraduationCap,
  PiggyBank,
  Repeat,
  HeartHandshake,
  BookOpen,
  CalendarDays,
} from "lucide-react";
import { LINKS, IMAGES } from "@/lib/constants";
import { TM } from "./TM";

/* Tool icon registry — small round badges shown inside each Principle card. */
const TOOL_ICONS = {
  shyft: { src: "/icons/shyft.png", label: "SHYFT" },
  shyftApp: { src: "/shyft-app-hero.png", label: "SHYFT App", crop: true },
  shyftasap: { src: "/coalition-emblem.png", label: "SHYFTASAP.com" },
  smartlock: { src: "/icons/smartlock.png", label: "SmartLock\u2122" },
  eazybot: {
    src: "/icons/eazybot.png",
    label: "EazyBot",
    padding: "p-1",
    breathe: true,
  },
  orb: { src: "/icons/orb.png", label: "ORB" },
};

const PILLARS = [
  {
    id: "01",
    name: "Raise Your Fi-Q",
    bullets: [
      {
        label: "The SHYFT",
        text: "Raising my Financial IQ is the key to my family\u2019s freedom.",
      },
      {
        label: "Strategy",
        text: "Join the SHYFT Coalition and stay engaged.",
      },
    ],
    toolsLabel: "Financial Tools",
    toolsText: "SHYFT Community, SHYFT App, SHYFT ORB",
    tools: ["shyft", "shyftApp", "orb"],
    Icon: GraduationCap,
  },
  {
    id: "02",
    name: "Exercise Conscious Spending",
    bullets: [
      {
        label: "The SHYFT",
        text: "I will always pay close attention to my spending habits.",
      },
      {
        label: "Strategy",
        text: "Document my spending in an IRS compliant manner.",
      },
    ],
    toolsLabel: "Financial Tools",
    toolsText: "SmartLock\u2122, EazyBot, SHYFT ORB",
    tools: ["smartlock", "eazybot", "orb"],
    Icon: PiggyBank,
  },
  {
    id: "03",
    name: "Auto-Pilot Your Income",
    bullets: [
      {
        label: "The SHYFT",
        text: "Trading my time for money is unacceptable.",
      },
      {
        label: "Strategy",
        text: "Work to create perpetual income, not paychecks.",
      },
    ],
    toolsLabel: "Financial Tools",
    toolsText: "EazyBot, SmartLock\u2122",
    tools: ["eazybot", "smartlock"],
    Icon: Repeat,
  },
  {
    id: "04",
    name: "Pay it Forward",
    bullets: [
      {
        label: "The SHYFT",
        text: "I have an obligation to financially uplift others.",
      },
      {
        label: "Strategy",
        text: "Share the information I\u2019m learning with my loved ones.",
      },
    ],
    toolsLabel: "Financial Tools",
    toolsText: "SHYFTClasses.com, SHYFTASAP.com, SmartLockMe.com, MyBotsPay.me",
    tools: ["shyft", "shyftasap", "smartlock", "eazybot"],
    toolLabels: [
      "SHYFTClasses.com",
      "SHYFTASAP.com",
      "SmartLockMe.com",
      "MyBotsPay.me",
    ],
    Icon: HeartHandshake,
  },
];

const ToolBadge = ({ toolKey, label }) => {
  const t = TOOL_ICONS[toolKey];
  if (!t) return null;
  const displayLabel = label || t.label;
  return (
    <span
      data-testid={`tool-icon-${toolKey}`}
      title={displayLabel}
      aria-label={displayLabel}
      className={`inline-flex items-center justify-center w-12 h-12 rounded-full border border-[rgba(30,144,255,0.5)] bg-[#0a0a0a] overflow-hidden shrink-0 transition hover:border-[#1e90ff] hover:shadow-[0_0_16px_-2px_rgba(30,144,255,0.7)] ${
        t.breathe ? "product-breathe" : ""
      }`}
    >
      <img
        src={t.src}
        alt={displayLabel}
        className={`w-full h-full ${t.crop ? "object-cover" : `object-contain ${t.padding || "p-1.5"}`}`}
        style={t.filter ? { filter: t.filter } : undefined}
        loading="lazy"
      />
    </span>
  );
};

export const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-40 pb-16 md:pt-48 md:pb-20 overflow-hidden"
    >
      {/* Spotlights */}
      <div
        aria-hidden
        className="vault-spotlight"
        style={{ top: "8%", left: "-10%", width: 520, height: 520 }}
      />
      <div
        aria-hidden
        className="vault-spotlight-cyan"
        style={{ top: "18%", right: "-12%", width: 680, height: 680, opacity: 0.7 }}
      />

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Mandate copy */}
        <div className="reveal text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[rgba(30,144,255,0.35)] bg-[#0b1422]/70 backdrop-blur">
            <ShieldCheck size={14} className="text-[#6fb3ff]" />
            <span className="eyebrow !text-[#6fb3ff]">
              The SHYFT Coalition<TM /> · Mandate
            </span>
            <span className="ml-1 status-dot" aria-hidden />
          </div>

          <h1 className="font-serif-display mt-6 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-white">
            The SHYFT Coalition<TM className="!text-[0.22em]" />
          </h1>

          <p className="font-serif-display mt-4 text-lg sm:text-xl lg:text-2xl leading-[1.3] tracking-tight text-gradient-gold max-w-2xl mx-auto">
            It&rsquo;s Time to Shift How You Financially Think
          </p>
        </div>

        <div className="reveal mt-10 max-w-3xl mx-auto space-y-5 text-[#cfc8b9] text-[15px] md:text-base leading-[1.75] text-pretty">
          <p>
            <strong className="text-white font-semibold">
              The SHYFT Coalition&rsquo;s
            </strong>{" "}
            mission is straight forward: to provide the education, systems,
            tools, support and culture you need to live a financially
            intelligent lifestyle.
          </p>
          <p>
            Our traditional education system (and society) is designed to
            turn us all into highly consuming, underpaid employees.
            We&rsquo;re inviting you to &lsquo;
            <strong className="text-white font-semibold">SHYFT</strong>
            &rsquo; from that unfulfilling life and encouraging you to become
            financially&nbsp;free.
          </p>
          <p>
            There is no cost to join the{" "}
            <strong className="text-white font-semibold">
              SHYFT Coalition&rsquo;s
            </strong>{" "}
            private online community.
          </p>
          <p>
            Once you&rsquo;re inside, you&rsquo;ll gain immediate access to
            an immersive learning environment with online classes, strategic
            fintech tools and the consistent support you need to help you{" "}
            <span className="italic text-gradient-gold font-semibold">
              Shift How You Financially Think.
            </span>
          </p>
          <p className="text-white font-semibold text-base md:text-lg pt-1">
            Welcome to the SHYFT Coalition!
          </p>
        </div>

        {/* SHYFT Lifestyle Principles */}
        <div id="pillars" className="mt-20 md:mt-24 scroll-mt-24">
          <div className="reveal flex items-center justify-center gap-4">
            <span className="h-px w-12 bg-[rgba(30,144,255,0.4)]" />
            <span className="eyebrow !text-[#6fb3ff]">
              SHYFT Lifestyle Principles
            </span>
            <span className="h-px w-12 bg-[rgba(30,144,255,0.4)]" />
          </div>

          {/* Ecosystem intro — same weight/styling as the body copy under "Learn to SHYFT" */}
          <div
            data-testid="pillars-signature"
            className="reveal mt-8 md:mt-10 max-w-3xl mx-auto text-center"
          >
            <p className="text-[#cfc8b9] text-[15px] md:text-base leading-[1.75]">
              Our <span className="text-gradient-gold font-semibold">SHYFT</span>{" "}
              Lifestyle Principles support the financially intelligent
              lifestyle we encourage everyone to live.
            </p>
          </div>

          <div className="mt-10 md:mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {PILLARS.map(
              ({
                id,
                name,
                bullets,
                tools,
                toolLabels,
                toolsLabel,
                toolsText,
                Icon,
              }) => (
                <article
                  key={id}
                  data-testid={`pillar-card-${id}`}
                  className="reveal group relative vault-card flex flex-col gap-4 min-h-[420px] overflow-hidden !p-7"
                >
                  <div
                    aria-hidden
                    className="absolute inset-0 rounded-[2.25rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                      boxShadow:
                        "inset 0 0 0 1px rgba(30,144,255,0.45), 0 0 40px -8px rgba(30,144,255,0.35)",
                    }}
                  />

                  <div className="relative flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className="eyebrow !text-[10px] !text-[#6fb3ff]">
                        Principle
                      </span>
                      <span className="font-serif-display text-xl text-gold leading-none">
                        {id}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="status-dot" aria-hidden />
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[rgba(201,162,91,0.4)] bg-[#0a0a0a]">
                        <Icon size={16} className="text-gold" />
                      </span>
                    </div>
                  </div>

                  <h3 className="relative font-serif-display text-[1.05rem] md:text-[1.1rem] leading-[1.25] text-white">
                    {name}
                  </h3>

                  <ul className="relative mt-1 space-y-3 text-[13px] leading-[1.55]">
                    {/* Bullet 1 — white */}
                    <li className="text-white">
                      <span className="font-semibold">{bullets[0].label}</span>{" "}
                      <span className="text-[#b8b3aa]">— {bullets[0].text}</span>
                    </li>
                    {/* Bullet 2 — blue label */}
                    <li className="text-[#6fb3ff]">
                      <span className="font-semibold">{bullets[1].label}</span>{" "}
                      <span className="text-[#b8b3aa]">— {bullets[1].text}</span>
                    </li>
                    {/* Bullet 3 — Financial Tools (gold label) with tool names text */}
                    <li
                      className="text-[#e7c98a]"
                      data-testid={`pillar-${id}-tools`}
                    >
                      <span className="font-semibold">{toolsLabel}</span>{" "}
                      <span className="text-[#b8b3aa]">— {toolsText}</span>
                    </li>
                  </ul>

                  <div className="relative mt-auto vault-divider" />

                  {/* Tool icon row — centered, replaces principle/live-coalition row */}
                  <div
                    className="relative flex items-center justify-center gap-2.5 pt-1"
                    data-testid={`pillar-${id}-icons`}
                  >
                    {tools.map((toolKey, idx) => (
                      <ToolBadge
                        key={`${id}-${toolKey}-${idx}`}
                        toolKey={toolKey}
                        label={toolLabels ? toolLabels[idx] : undefined}
                      />
                    ))}
                  </div>
                </article>
              ),
            )}
          </div>
        </div>

        {/* Education Section */}
        <div
          id="education"
          data-testid="education-section"
          className="reveal mt-24 md:mt-28 scroll-mt-24 max-w-4xl mx-auto"
        >
          <div className="text-center">
            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[rgba(30,144,255,0.35)] bg-[#0b1422]/70 backdrop-blur">
              <BookOpen size={14} className="text-[#6fb3ff]" />
              <span className="eyebrow !text-[#6fb3ff]">Education</span>
              <span className="ml-1 status-dot" aria-hidden />
            </div>

            <h2 className="font-serif-display mt-5 text-2xl md:text-3xl lg:text-[2rem] leading-[1.15] text-white">
              Learn to <span className="text-gradient-gold">SHYFT</span>
            </h2>
          </div>

          <div className="mt-8 space-y-5 text-[#cfc8b9] text-[15px] md:text-base leading-[1.75]">
            <p>
              Our private online community is based on the award winning Mighty
              Networks platform and serves as the online &lsquo;headquarters&rsquo;
              of the SHYFT Coalition<TM />. We offer a dynamic learning experience
              supported by strategic education academies, a proprietary
              financial ecosystem, and a family-oriented networking haven for
              everyone to grow.
            </p>
          </div>

          {/* Sub-block: Weekly Online Classes */}
          <div className="mt-10 rounded-[2rem] border border-[rgba(201,162,91,0.2)] bg-[#14110e]/55 backdrop-blur p-6 md:p-8">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[rgba(201,162,91,0.4)] bg-[#0a0a0a]">
                <CalendarDays size={16} className="text-gold" />
              </span>
              <h3 className="font-serif-display text-lg md:text-xl leading-tight text-white">
                Weekly Online Classes
              </h3>
              <span className="ml-auto status-dot" aria-hidden />
            </div>

            <div className="mt-5 space-y-4 text-[#cfc8b9] text-[14px] md:text-[15px] leading-[1.75]">
              <p>
                The first thing we encourage you to do is RSVP for each of our
                weekly online classes, even if they conflict with your current
                schedule. This allows you to stay updated on the information
                we&rsquo;re teaching and provides a direct link to each
                class&rsquo;s recordings.
              </p>
              <p>
                You can also click the{" "}
                <span className="text-gold font-medium">SHYFT Classes</span>{" "}
                button below for an overview of our weekly class schedule and
                direct links to attend each class.
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href={LINKS.community}
              target="_blank"
              rel="noreferrer"
              data-testid="education-join-cta"
              className="btn-gold"
            >
              Join Our Community
              <ArrowUpRight size={18} strokeWidth={2.4} />
            </a>
            <a
              href={LINKS.shyftClasses}
              target="_blank"
              rel="noreferrer"
              data-testid="education-classes-cta"
              className="btn-outline-gold"
            >
              SHYFTClasses.com
              <ArrowUpRight size={16} strokeWidth={2.4} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
